<template>
    <div :class="{opened}" class="dialog" v-if="snapshot">
        <header>
            <h3>Sharing options</h3>
            <Icon @click.native="opened = false" name="close"/>
        </header>
        <section>
            <h4>Public access</h4>
            <div class="buttons">
                <NiceButton :disabled="snapshot.public"
                            :secondary="snapshot.public"
                            @click="setVisibility(true)"
                            icon="public"
                            title="Publish"/>
                <NiceButton
                        :disabled="!snapshot.public"
                        :secondary="!snapshot.public"
                        @click="setVisibility(false)"
                        icon="public_off"
                        title="Unpublish"/>
                <NiceButton
                        :disabled="!snapshot.public"
                        @click="share"
                        icon="share"
                        secondary
                        v-if="canShare"/>
                <NiceButton
                        :disabled="!snapshot.public"
                        @click="copyLink"
                        icon="content_copy"
                        secondary/>
            </div>
        </section>

        <section>
            <h4>Short link</h4>
            <EndLinkInput :default-value="newLinkName"
                          constant-value="https://shrink.web.app/"
                          v-model="newLinkName"/>
            <p :class="{active: newLinkName===shortNameThatAlreadyExist && shortNameThatAlreadyExist }"
               class="link-in-use">
                This name is already used
            </p>
            <div class="buttons">
                <NiceButton
                        @click="share"
                        icon="share"
                        secondary
                        v-if="canShare"/>
                <NiceButton
                        :disabled="shortLinkEntry ? (shortLinkEntry.id === newLinkName) : !newLinkName"
                        :icon="shortLinkEntry ? 'update' : 'add'"
                        @click="createShortLink"
                        secondary/>

                <NiceButton
                        :disabled="!shortLinkEntry"
                        @click="copyShortLink"
                        icon="content_copy"
                        secondary/>

                <NiceButton
                        :disabled="!shortLinkEntry"
                        @click="deleteShortLink"
                        icon="delete"
                        secondary/>
            </div>

        </section>
        <p :class="{active: waitingForChanges1 || waitingForChanges2}" class="setting-value">Waiting for connection</p>
    </div>
</template>

<!--suppress JSUnusedGlobalSymbols -->
<script>
    import * as firebase from 'firebase/app';
    import 'firebase/firestore';
    import 'firebase/auth';
    import NiceButton from '../components/NiceButton';
    import EndLinkInput from '../components/EndLinkInput';
    import Icon from '../components/Icon';

    export default {
        name: 'SharingOptionsDialog',
        components: {Icon, EndLinkInput, NiceButton},
        props: {
            fileId: {
                type: String,
                required: true
            },
            openTime: Number
        },
        watch: {
            openTime: {
                deep: true,
                immediate: true,
                handler(newValue, oldValue) {
                    if (newValue !== oldValue)
                        this.opened = true;
                },
            }
        },
        data() {
            return {
                waitingForChanges1: false,
                waitingForChanges2: false,
                snapshot: null,
                canShare: !!navigator.share,
                shortLinkEntry: null,
                shortNameThatAlreadyExist: null,
                newLinkName: null,
                opened: true,
            };
        },
        created() {
            this.uid = firebase.auth().currentUser.uid;
            // noinspection JSCheckFunctionSignatures
            this.sub1 = firebase
                .firestore()
                .collection('files')
                .doc(this.fileId)
                .onSnapshot({
                    includeMetadataChanges: true
                }, this.onSnapshot);

            this.sub2 = firebase
                .firestore()
                .collection('links')
                .where('uid', '==', this.uid)
                .where('file', '==', this.fileId)
                .limit(1)
                .onSnapshot({
                    includeMetadataChanges: true,
                }, {
                    next: this.onLinkSnapshot,
                });
        },
        destroyed() {
            this.sub1();
            this.sub2();
        },
        methods: {
            onLinkSnapshot(snap) {
                const wasNull = !this.shortLinkEntry;
                this.shortLinkEntry = snap.docs.map(e => ({
                    ...e.data(),
                    id: e.id,
                    pending: e.metadata.hasPendingWrites
                }))[0];
                if (wasNull && this.shortLinkEntry)
                    this.newLinkName = this.shortLinkEntry.id;
                this.waitingForChanges2 = this.shortLinkEntry && this.shortLinkEntry.pending;
            },
            createShortLink() {
                const file = {
                    file: this.fileId,
                    uid: this.uid,
                    url: window.location.href,
                    created: firebase.firestore.FieldValue.serverTimestamp()
                };

                firebase
                    .firestore()
                    .collection('links')
                    .doc(this.newLinkName)
                    .set(file)
                    .catch(e => {
                        if (e.code === 'permission-denied')
                            this.shortNameThatAlreadyExist = name;
                        else
                            alert(e.message);
                    });

                if (this.shortLinkEntry) {
                    firebase.firestore()
                            .collection('links')
                            .doc(this.shortLinkEntry.id)
                            .delete();
                }
            },
            deleteShortLink() {
                if (!this.shortLinkEntry) return;
                firebase.firestore()
                        .collection('links')
                        .doc(this.shortLinkEntry.id)
                        .delete();
                this.shortLinkEntry = null;
            },
            share() {
                navigator.share({
                    text: 'Here you\'ve got ' + this.fileName,
                    title: this.fileName,
                    url: window.location.href
                });
            },
            copyShortLink() {
                navigator.clipboard.writeText('https://shrink.web.app/' + this.shortLinkEntry.id);
            },
            copyLink() {
                navigator.clipboard.writeText(window.location.href);
            },
            onSnapshot(snap) {
                this.waitingForChanges1 = snap.metadata.hasPendingWrites;
                this.snapshot = snap.data();
            },
            setVisibility(public_) {
                if (this.snapshot.public === !!public_) return;
                firebase.firestore()
                        .collection('files')
                        .doc(this.fileId)
                        .update({public: !!public_});
            }
        }
    };
</script>

<!--suppress CssUnusedSymbol -->
<style scoped>
    .dialog {
        opacity: 0;
        pointer-events: none;
        transition: opacity .3s;
        position: absolute;
        animation: FadeIn .3s;
        background-color: #0c0c2a;
        border-radius: 8px;
        top: 8px;
        left: 8px;
        right: 8px;
        overflow-x: auto;
        z-index: 30;
        max-width: 400px;
        box-shadow: 1px 2px 8px 0 rgba(0, 0, 0, .7);
    }

    .dialog.opened {
        pointer-events: unset;
        opacity: 1;
    }

    h3 {
        margin: 6px;
    }

    .setting-value {
        margin: 2px;
        opacity: 0;
        font-size: .9em;
        font-weight: 600;
        transition: opacity 1s;
    }

    .setting-value.active {
        opacity: .8;
        transition-delay: 2s;
    }

    section {
        border-top: 1px solid #ffffff22;
        padding: 0 4px;
    }

    h4 {
        margin: 4px 8px;
        text-align: left;
    }

    .buttons {
        font-size: .9em;
        display: flex;
        justify-content: center;
        align-items: center;
        flex-flow: row wrap;
        justify-items: center;
    }

    .buttons > * {
        margin: 2px;
    }

    .secondary.buttons {
        font-size: .8em;
    }

    .link-in-use {
        margin: 2px;
        color: #f84f4f;
        opacity: 0;
        transition: opacity .3s;
    }

    .link-in-use.active {
        opacity: 1;
    }

    header {
        padding: 0 4px;
        display: grid;
        grid-template-areas: '. title close';
        align-items: center;
        grid-template-columns: 1.6em 1fr 1.6em;
    }

    header h3 {
        grid-area: title;
    }

    header .icon {
        grid-area: close;
        cursor: pointer;
    }
</style>
